import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, PackageSearch, ListChecks, Clock, Upload, Settings as Cog, Search, ShieldCheck, LogOut, Users as UsersIcon, History, KeyRound, Truck, Award } from "lucide-react";
import { useItemDetail } from "@/lib/itemDetail";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { changePassword } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/advisor", label: "Inventory Advisor", icon: PackageSearch },
  { to: "/bulk", label: "Bulk Item Lookup", icon: ListChecks },
  { to: "/procurement", label: "Procurement Snapshot", icon: Truck, admin: true },
  { to: "/preferred-supplier", label: "Preferred Supplier", icon: Award, admin: true },
  { to: "/lead-times", label: "Lead Time Manager", icon: Clock },
  { to: "/upload", label: "Data Upload", icon: Upload },
  { to: "/settings", label: "Settings", icon: Cog },
  { to: "/users", label: "User Management", icon: UsersIcon, admin: true },
  { to: "/audit", label: "Change Audit", icon: History, admin: true },
];

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await changePassword({ current_password: cur, new_password: next });
      toast.success("Password changed");
      onOpenChange(false); setCur(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change password"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Change Password</DialogTitle>
          <DialogDescription>Update the password for your own account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {[["Current Password", cur, setCur, "change-pw-current"], ["New Password", next, setNext, "change-pw-new"], ["Confirm New Password", confirm, setConfirm, "change-pw-confirm"]].map(([l, v, set, tid]) => (
            <div key={tid}><div className="label-eyebrow mb-1">{l}</div>
              <input type="password" value={v} onChange={(e) => set(e.target.value)} data-testid={tid}
                className="w-full h-9 rounded-md border border-[var(--line)] px-3 text-[13px] outline-none focus:border-slate-400" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="submit-change-password" className="h-9 px-5 rounded-md text-white text-sm font-medium disabled:opacity-50" style={{ background: "var(--kcsa-red)" }}>{busy ? "Saving…" : "Update Password"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout({ children }) {
  const [q, setQ] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();
  const { openItem } = useItemDetail();
  const { user, signOut, isAdmin } = useAuth();

  const onSearch = (e) => {
    e.preventDefault();
    const codes = q.split(/[\s,;]+/).filter(Boolean);
    if (codes.length === 1 && /^\d+$/.test(codes[0])) {
      openItem(Number(codes[0]));
    } else if (codes.length) {
      navigate(`/advisor?search=${encodeURIComponent(q.trim())}`);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--surface)]">
      {/* Sidebar */}
      <aside className="w-[232px] shrink-0 bg-white border-r border-[var(--line)] flex flex-col sticky top-0 h-screen">
        <div className="h-[52px] flex items-center gap-2 px-5 border-b border-[var(--line)]">
          <ShieldCheck className="w-5 h-5" style={{ color: "var(--kcsa-red)" }} />
          <div className="leading-none">
            <div className="text-[13px] font-bold tracking-wide text-slate-900">StockFlow</div>
            <div className="text-[9.5px] label-eyebrow mt-0.5">Inventory Advisor</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2.5 space-y-0.5">
          {NAV.filter((n) => !n.admin || isAdmin).map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${
                  isActive ? "bg-red-50 text-[var(--kcsa-red-dark)]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="w-[17px] h-[17px]" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-3 border-t border-[var(--line)]">
          <div className="label-eyebrow">Mining Procurement</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Operational View</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[52px] flex items-center gap-4 px-6 text-white sticky top-0 z-30" style={{ background: "var(--kcsa-red)" }}>
          <div className="text-[15px] font-semibold tracking-tight">StockFlow</div>
          <form onSubmit={onSearch} className="ml-auto relative w-[380px] max-w-[46vw]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/70" />
            <input
              data-testid="global-item-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Item Code(s)… paste from Excel"
              className="w-full h-9 pl-9 pr-3 rounded-md bg-white/15 placeholder-white/60 text-white text-[13px] outline-none focus:bg-white/25 border border-white/20"
            />
          </form>
          <div className="text-[10.5px] label-eyebrow text-white/80 hidden lg:block">StockFlow System</div>
          {user && (
            <div className="flex items-center gap-2.5">
              <div className="text-right leading-tight hidden sm:block">
                <div className="text-[12.5px] font-semibold">{user.name}</div>
                <div className="text-[9.5px] uppercase tracking-wider text-white/75">{user.role === "admin" ? "Administrator" : "Read Only"}</div>
              </div>
              <button onClick={() => setPwOpen(true)} data-testid="change-password-btn" title="Change password"
                className="w-9 h-9 grid place-items-center rounded-md bg-white/15 hover:bg-white/25 border border-white/20">
                <KeyRound className="w-4 h-4" />
              </button>
              <button onClick={signOut} data-testid="logout-btn" title="Sign out"
                className="w-9 h-9 grid place-items-center rounded-md bg-white/15 hover:bg-white/25 border border-white/20">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
